import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

function normalizeOption(option, index) {
  if (typeof option === "string" || typeof option === "number") {
    const value = String(option);
    return {
      key: `${value}-${index}`,
      value,
      label: value,
      disabled: false,
    };
  }

  if (option && typeof option === "object") {
    const value = String(option.value ?? option.id ?? option.key ?? option.label ?? option.name ?? "");
    const label = String(option.label ?? option.name ?? value);
    return {
      key: String(option.key ?? `${value}-${index}`),
      value,
      label,
      disabled: Boolean(option.disabled),
    };
  }

  return {
    key: `option-${index}`,
    value: "",
    label: "",
    disabled: false,
  };
}

function getFirstEnabledIndex(options) {
  return options.findIndex((option) => !option.disabled);
}

function getLastEnabledIndex(options) {
  for (let index = options.length - 1; index >= 0; index -= 1) {
    if (!options[index]?.disabled) return index;
  }
  return -1;
}

function getNextEnabledIndex(options, startIndex, direction) {
  if (!Array.isArray(options) || options.length === 0) return -1;

  const fallback = direction > 0 ? getFirstEnabledIndex(options) : getLastEnabledIndex(options);
  if (startIndex < 0) return fallback;

  let index = startIndex;
  for (let remaining = options.length; remaining > 0; remaining -= 1) {
    index = (index + direction + options.length) % options.length;
    if (!options[index]?.disabled) return index;
  }

  return fallback;
}

export default function ShopSelect({
  value,
  onValueChange,
  options = [],
  placeholder = "Select an option",
  disabled = false,
  invalid = false,
  variant = "default",
  triggerClassName = "",
  panelClassName = "",
  optionClassName = "",
  align = "start",
  minPanelWidth = 180,
  menuZIndex = 220,
  ariaLabel,
  hideIndicator = false,
}) {
  const triggerId = useId();
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const optionRefs = useRef([]);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [panelStyle, setPanelStyle] = useState({});

  const normalizedOptions = useMemo(
    () => options.map((option, index) => normalizeOption(option, index)).filter((option) => option.label),
    [options]
  );

  const selectedIndex = useMemo(
    () => normalizedOptions.findIndex((option) => option.value === String(value ?? "")),
    [normalizedOptions, value]
  );

  const selectedOption = selectedIndex >= 0 ? normalizedOptions[selectedIndex] : null;
  const listboxId = `${triggerId}-listbox`;
  const triggerVariantClass = variant === "ghost" ? "theme-select-trigger-ghost" : "theme-select-trigger";
  const invalidStateClass = invalid
    ? "border-rose-400 focus-visible:border-rose-500 focus-visible:ring-rose-500/20 dark:border-rose-500 dark:focus-visible:border-rose-400 dark:focus-visible:ring-rose-500/30"
    : "";

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const horizontalPadding = 12;
    const verticalPadding = 12;
    const desiredWidth = Math.max(minPanelWidth, rect.width);
    const panelWidth = Math.min(desiredWidth, viewportWidth - horizontalPadding * 2);
    const availableBelow = viewportHeight - rect.bottom - verticalPadding;
    const availableAbove = rect.top - verticalPadding;
    const renderAbove = availableBelow < 220 && availableAbove > availableBelow;
    const maxHeight = Math.max(
      160,
      Math.min(320, (renderAbove ? availableAbove : availableBelow) - 8)
    );

    const leftBase = align === "end" ? rect.right - panelWidth : rect.left;
    const left = Math.min(
      Math.max(horizontalPadding, leftBase),
      viewportWidth - panelWidth - horizontalPadding
    );

    const nextPanelStyle = renderAbove
      ? {
          position: "fixed",
          left,
          bottom: Math.max(verticalPadding, viewportHeight - rect.top + 8),
          width: panelWidth,
          maxHeight,
          zIndex: menuZIndex,
          // A modal Dialog turns pointer events off outside itself; this panel is portaled to the body, so it
          // opts back in, or a mouse could not pick an option inside a dialog (APP-HR-076).
          pointerEvents: "auto",
        }
      : {
          position: "fixed",
          left,
          top: Math.max(verticalPadding, rect.bottom + 8),
          width: panelWidth,
          maxHeight,
          zIndex: menuZIndex,
          // A modal Dialog turns pointer events off outside itself; this panel is portaled to the body, so it
          // opts back in, or a mouse could not pick an option inside a dialog (APP-HR-076).
          pointerEvents: "auto",
        };

    setPanelStyle((currentPanelStyle) => {
      const positionUnchanged =
        currentPanelStyle.position === nextPanelStyle.position &&
        currentPanelStyle.left === nextPanelStyle.left &&
        currentPanelStyle.top === nextPanelStyle.top &&
        currentPanelStyle.bottom === nextPanelStyle.bottom &&
        currentPanelStyle.width === nextPanelStyle.width &&
        currentPanelStyle.maxHeight === nextPanelStyle.maxHeight &&
        currentPanelStyle.zIndex === nextPanelStyle.zIndex;

      return positionUnchanged ? currentPanelStyle : nextPanelStyle;
    });
  }, [align, menuZIndex, minPanelWidth]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const moveFocusFromTrigger = useCallback((direction) => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    window.requestAnimationFrame(() => {
      const focusable = Array.from(document.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((element) => {
          if (!(element instanceof HTMLElement)) return false;
          if (element === trigger) return true;
          if (element.getAttribute("aria-hidden") === "true") return false;
          return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
        });

      const currentIndex = focusable.indexOf(trigger);
      if (currentIndex < 0) {
        trigger.focus();
        return;
      }

      const nextIndex = direction < 0 ? currentIndex - 1 : currentIndex + 1;
      const nextTarget = focusable[nextIndex];
      if (nextTarget instanceof HTMLElement) {
        nextTarget.focus();
      } else {
        trigger.focus();
      }
    });
  }, []);

  const focusTrigger = useCallback(() => {
    window.requestAnimationFrame(() => {
      triggerRef.current?.focus({ preventScroll: true });
    });
  }, []);

  const commitSelection = useCallback(
    (option) => {
      if (!option || option.disabled) return;
      onValueChange?.(option.value, option);
      closeMenu();
      focusTrigger();
    },
    [closeMenu, focusTrigger, onValueChange]
  );

  // The panel is portaled to the document body, so calculate its fixed position
  // before the browser paints it. A normal effect allows one unpositioned frame,
  // which is especially visible as a jump when the select is inside a modal.
  useLayoutEffect(() => {
    if (!open) return;
    updatePanelPosition();
  }, [open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const onResize = () => updatePanelPosition();
    const onScroll = () => updatePanelPosition();
    const onPointerDown = (event) => {
      const target = event.target;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedPanel = panelRef.current?.contains(target);
      if (!clickedTrigger && !clickedPanel) closeMenu();
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [closeMenu, open, updatePanelPosition]);

  useEffect(() => {
    if (!open) return undefined;

    const nextIndex = selectedIndex >= 0 ? selectedIndex : getFirstEnabledIndex(normalizedOptions);
    setHighlightedIndex(nextIndex);

    const rafId = window.requestAnimationFrame(() => {
      panelRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [normalizedOptions, open, selectedIndex]);

  useEffect(() => {
    if (!open || highlightedIndex < 0) return;

    const panel = panelRef.current;
    const option = optionRefs.current[highlightedIndex];
    if (!panel || !option) return;

    const optionTop = option.offsetTop;
    const optionBottom = optionTop + option.offsetHeight;
    const visibleTop = panel.scrollTop;
    const visibleBottom = visibleTop + panel.clientHeight;

    if (optionTop < visibleTop) {
      panel.scrollTop = optionTop;
    } else if (optionBottom > visibleBottom) {
      panel.scrollTop = optionBottom - panel.clientHeight;
    }
  }, [highlightedIndex, open]);

  const moveHighlight = useCallback(
    (direction) => {
      setHighlightedIndex((current) => getNextEnabledIndex(normalizedOptions, current, direction));
    },
    [normalizedOptions]
  );

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlightedIndex(getNextEnabledIndex(normalizedOptions, selectedIndex, 1));
      } else {
        moveHighlight(1);
      }
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlightedIndex(getNextEnabledIndex(normalizedOptions, selectedIndex, -1));
      } else {
        moveHighlight(-1);
      }
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      setOpen(true);
      setHighlightedIndex(getFirstEnabledIndex(normalizedOptions));
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      setOpen(true);
      setHighlightedIndex(getLastEnabledIndex(normalizedOptions));
    }
  };

  const handlePanelKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      setHighlightedIndex(getFirstEnabledIndex(normalizedOptions));
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      setHighlightedIndex(getLastEnabledIndex(normalizedOptions));
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (highlightedIndex >= 0) commitSelection(normalizedOptions[highlightedIndex]);
      return;
    }

    if (event.key === "Escape") {
      closeMenu();
      focusTrigger();
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      const direction = event.shiftKey ? -1 : 1;
      closeMenu();
      moveFocusFromTrigger(direction);
    }
  };

  const triggerLabel = selectedOption?.label || placeholder;

  return (
    <>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
        className={[
          triggerVariantClass,
          "focus-visible:ring-2 focus-visible:ring-neutral-400/70 dark:focus-visible:ring-neutral-500/70",
          invalidStateClass,
          open && variant === "default" ? "border-neutral-400 ring-2 ring-neutral-900/10 dark:border-neutral-500 dark:ring-neutral-500/10" : "",
          open && variant === "ghost" ? "text-neutral-900 dark:text-neutral-50" : "",
          triggerClassName,
        ].join(" ")}
      >
        <span className={`min-w-0 flex-1 truncate ${selectedOption ? "" : "theme-select-placeholder"}`}>
          {triggerLabel}
        </span>
        {!hideIndicator ? (
          <ChevronDown
            size={16}
            className={`theme-select-caret ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        ) : null}
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          tabIndex={-1}
          role="listbox"
          id={listboxId}
          aria-labelledby={!ariaLabel ? triggerId : undefined}
          aria-label={ariaLabel}
          aria-activedescendant={highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined}
          style={panelStyle}
          className={`theme-menu-panel overflow-y-auto ${panelClassName}`}
          onKeyDown={handlePanelKeyDown}
        >
          {normalizedOptions.map((option, index) => {
            const isHighlighted = index === highlightedIndex;
            const isSelected = index === selectedIndex;

            return (
              <button
                key={option.key}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={isSelected}
                disabled={option.disabled}
                onMouseEnter={() => {
                  if (!option.disabled) setHighlightedIndex(index);
                }}
                onClick={() => commitSelection(option)}
                className={[
                  "theme-menu-item w-full rounded-none px-4 py-2.5",
                  isHighlighted || isSelected ? "theme-menu-item-active" : "",
                  option.disabled ? "theme-menu-item-disabled" : "",
                  optionClassName,
                ].join(" ")}
              >
                <span className={`min-w-0 flex-1 truncate ${option.disabled ? "theme-menu-item-subtle" : ""}`}>
                  {option.label}
                </span>
                <span className="ml-auto flex h-4 w-4 items-center justify-center">
                  {isSelected ? <Check size={14} className="theme-menu-item-icon" /> : null}
                </span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </>
  );
}
